import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  DatePicker,
  Button,
  Space,
  Table,
  Input,
  Tag,
  Tooltip,
  Row,
  Col,
  message,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function LaporanPemasukanPerDokPabean() {
  const [range, setRange] = useState([]);
  const [nopen, setNopen] = useState("");
  const [kodeBarang, setKodeBarang] = useState("");
  const [namaBarang, setNamaBarang] = useState("");
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchData = useCallback(
    async (
      page = 1,
      pageSize = 20,
      dateRange = range,
      nopenVal = nopen,
      kodeVal = kodeBarang,
      namaVal = namaBarang
    ) => {
      setLoading(true);
      try {
        const user = getUser();
        const userLevel = String(user?.id_user_level || user?.id_level || "");
        const params = {
          page,
          limit: pageSize,
        };

        if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
          params.start_date = dateRange[0].format("YYYY-MM-DD");
          params.end_date = dateRange[1].format("YYYY-MM-DD");
        }
        if (nopenVal && nopenVal.trim()) {
          params.nopen = nopenVal.trim();
        }
        if (kodeVal && kodeVal.trim()) {
          params.kode_barang = kodeVal.trim();
        }
        if (namaVal && namaVal.trim()) {
          params.nama_barang = namaVal.trim();
        }
        if (userLevel === "7" && user.username) {
          params.id_customer = user.username;
        }

        const res = await axiosInstance.get("/report/pemasukan-pabean", {
          params,
        });

        setData(res.data.data || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.limit || pageSize,
          total: res.data.total || 0,
        });
      } catch (e) {
        console.error(e);
        message.error("Gagal memuat laporan pemasukan");
      } finally {
        setLoading(false);
      }
    },
    [range, nopen, kodeBarang, namaBarang]
  );

  useEffect(() => {
    fetchData(1, 20, range, nopen, kodeBarang, namaBarang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData(1, pagination.pageSize, range, nopen, kodeBarang, namaBarang);
  };

  const handleReset = () => {
    setRange([]);
    setNopen("");
    setKodeBarang("");
    setNamaBarang("");
    fetchData(1, pagination.pageSize, [], "", "", "");
  };

  const handleTableChange = (newPagination) => {
    const page =
      newPagination.pageSize !== pagination.pageSize ? 1 : newPagination.current;
    fetchData(page, newPagination.pageSize, range, nopen, kodeBarang, namaBarang);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const user = getUser();
      const userLevel = String(user?.id_user_level || user?.id_level || "");

      const params = {};
      if (range && range.length === 2 && range[0] && range[1]) {
        params.start_date = range[0].format("YYYY-MM-DD");
        params.end_date = range[1].format("YYYY-MM-DD");
      }
      if (nopen && nopen.trim()) params.nopen = nopen.trim();
      if (kodeBarang && kodeBarang.trim()) params.kode_barang = kodeBarang.trim();
      if (namaBarang && namaBarang.trim()) params.nama_barang = namaBarang.trim();
      if (userLevel === "7" && user.username) {
        params.id_customer = user.username;
      }

      const response = await axiosInstance.get("/report/pemasukan-pabean/export", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const dateLabel =
        params.start_date && params.end_date
          ? `${params.start_date}_${params.end_date}`
          : dayjs().format("YYYY-MM-DD");
      link.download = `laporan_pemasukan_pabean_${dateLabel}.xlsx`;
      link.click();
      message.success("Export Excel berhasil diunduh");
    } catch (err) {
      console.error(err);
      message.error("Gagal mengunduh file excel");
    } finally {
      setExportLoading(false);
    }
  };

  const formatNumber = (num, decimals = 0) => {
    const n = Number(num || 0);
    return n.toLocaleString("id-ID", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 55,
      align: "center",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Data Dok Pabean",
      children: [
        {
          title: "Jenis",
          dataIndex: "jenis_dokumen",
          key: "jenis_dokumen",
          width: 85,
          align: "center",
          render: (val) => <Tag color="blue">{val || "BC 1.6"}</Tag>,
        },
        {
          title: "No. Daftar",
          dataIndex: "document_number",
          key: "document_number",
          width: 110,
          align: "center",
          render: (val) => <Text strong>{val || "-"}</Text>,
        },
        {
          title: "Tgl. Daftar",
          dataIndex: "document_date",
          key: "document_date",
          width: 105,
          align: "center",
          render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
      ],
    },
    {
      title: "Bukti Penerimaan Barang / GRN",
      children: [
        {
          title: "No BPB",
          dataIndex: "bpb_number",
          key: "bpb_number",
          width: 140,
          align: "center",
          render: (val) => val || "-",
        },
        {
          title: "Tanggal BPB",
          dataIndex: "bpb_date",
          key: "bpb_date",
          width: 105,
          align: "center",
          render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
      ],
    },
    {
      title: "Pengirim / Pemasok",
      dataIndex: "pengirim_barang",
      key: "pengirim_barang",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Pemilik Barang",
      dataIndex: "nama_pemilik",
      key: "nama_pemilik",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Kode Barang",
      dataIndex: "item_code",
      key: "item_code",
      width: 120,
      align: "center",
      render: (val) => <Tag color="geekblue">{val}</Tag>,
    },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      key: "item_name",
      width: 240,
      ellipsis: { showTitle: false },
      render: (val) => (
        <Tooltip placement="topLeft" title={val}>
          <Text strong>{val || "-"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Kemasan (Karton)",
      key: "kemasan",
      width: 125,
      align: "right",
      render: (_, r) => (
        <Space orientation="horizontal" size={4}>
          <Text strong>{formatNumber(r.jumlah_kemasan)}</Text>
          <Text orientation="secondary" style={{ fontSize: 11 }}>{r.nama_kemasan || "CT"}</Text>
        </Space>
      ),
    },
    {
      title: "Subsatuan (Botol/Can)",
      key: "subsatuan",
      width: 140,
      align: "right",
      render: (_, r) => (
        <Space orientation="horizontal" size={4}>
          <Text>{formatNumber(r.jumlah_subsatuan)}</Text>
          <Text orientation="secondary" style={{ fontSize: 11 }}>{r.nama_subsatuan || "BO"}</Text>
        </Space>
      ),
    },
    {
      title: "Satuan (LTR)",
      key: "satuan_ltr",
      width: 130,
      align: "right",
      render: (_, r) => (
        <Space orientation="horizontal" size={4}>
          <Text style={{ color: "#1890ff" }}>{formatNumber(r.jumlah_satuan, 2)}</Text>
          <Text orientation="secondary" style={{ fontSize: 11 }}>{r.nama_satuan || "LTR"}</Text>
        </Space>
      ),
    },
    {
      title: "Nilai Barang",
      dataIndex: "nilai_barang",
      key: "nilai_barang",
      align: "right",
      width: 130,
      render: (val) =>
        Number(val || 0).toLocaleString("id-ID", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Kondisi",
      dataIndex: "kondisi_barang",
      key: "kondisi_barang",
      align: "center",
      width: 95,
      render: (val) => <Tag color="green">{val || "BAIK"}</Tag>,
    },
  ];

  return (
    <Card style={{ borderRadius: 10, marginBottom: 40 }} bodyStyle={{ padding: "20px" }}>
      {/* Header: Title, Description & Export Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div style={{ maxWidth: 850 }}>
          <Title level={4} style={{ marginBottom: 4, fontWeight: 700, color: "#1f2937" }}>
            Laporan Pemasukan Barang Per Dokumen Pabean
          </Title>
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Daftar dokumen pabean pemasukan barang lengkap dengan rincian Kemasan (CT), Subsatuan (BO/CAN/PCE), dan Satuan Standar (LTR).
          </Text>
        </div>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={exportLoading}
          style={{
            borderColor: "#52c41a",
            color: "#52c41a",
            fontWeight: 600,
            height: 38,
            borderRadius: 6,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Export Excel
        </Button>
      </div>

      {/* Filter Card */}
      <Card
        size="small"
        style={{
          background: "#fafafa",
          borderRadius: 8,
          marginBottom: 20,
          border: "1px solid #e8e8e8",
        }}
        bodyStyle={{ padding: "16px 18px" }}
      >
        {/* Baris 1: 4 Kolom Filter Input */}
        <Row gutter={[16, 14]}>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#4b5563" }}>
              Periode Dokumen
            </Text>
            <RangePicker
              value={range}
              onChange={(val) => setRange(val || [])}
              format="DD-MM-YYYY"
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#4b5563" }}>
              No. Dokumen / Nopen
            </Text>
            <Input
              placeholder="Cari no. nopen / dokumen..."
              value={nopen}
              onChange={(e) => setNopen(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<FileTextOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#4b5563" }}>
              Kode Barang
            </Text>
            <Input
              placeholder="Cari kode barang..."
              value={kodeBarang}
              onChange={(e) => setKodeBarang(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<BarcodeOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ fontSize: 12, display: "block", marginBottom: 6, color: "#4b5563" }}>
              Nama Barang
            </Text>
            <Input
              placeholder="Cari nama barang..."
              value={namaBarang}
              onChange={(e) => setNamaBarang(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<ShoppingOutlined style={{ color: "#bfbfbf" }} />}
              allowClear
              style={{ width: "100%", height: 38, borderRadius: 6 }}
            />
          </Col>
        </Row>

        {/* Divider pemisah antara input dan baris tombol aksi */}
        <div style={{ margin: "14px 0", height: 1, background: "#f0f0f0" }} />

        {/* Baris 2: Tombol Aksi di Kanan */}
        <Row justify="end" align="middle">
          <Col xs={24} sm={12} style={{ textAlign: "right" }}>
            <Space size={10} wrap style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                style={{
                  minWidth: 110,
                  height: 38,
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                Cari Data
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                style={{
                  minWidth: 90,
                  height: 38,
                  borderRadius: 6,
                }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
        💡 Geser tabel ke kanan untuk melihat rincian Subsatuan dan Satuan Standar.
      </Text>

      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record, index) =>
          `${record.id_incoming || "in"}-${record.item_code || "item"}-${index}`
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          responsive: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, r) => `${r[0]}-${r[1]} dari ${total.toLocaleString("id-ID")} data`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1900 }}
      />
    </Card>
  );
}
